import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Home, HeartPulse, Info, MapPin, ArrowUpRight } from 'lucide-react';
import { api } from '../api';

function Pill({ children, tone = 'neutral' }) {
  const tones = {
    neutral: 'bg-[#F5EFDD] text-[#5A6660]',
    green: 'bg-[#E5F0EA] text-[#2D5F4F]',
    blue: 'bg-[#E0EAF8] text-[#34538F]',
    amber: 'bg-[#FBE6CC] text-[#A06628]',
  };
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${tones[tone]}`}>{children}</span>;
}

function Star({ r }) {
  if (r == null) return <span className="text-[10.5px] text-[#A8B0AA]">—</span>;
  return <span className="text-[12.5px] font-semibold text-[#A06628] tabular-nums">{Number(r).toFixed(1)}<span className="text-[#D4A85C] ml-0.5">★</span></span>;
}

function ServiceDots({ row }) {
  const items = [
    { k: 'offers_nursing', label: 'RN' },
    { k: 'offers_pt', label: 'PT' },
    { k: 'offers_ot', label: 'OT' },
    { k: 'offers_slp', label: 'SLP' },
    { k: 'offers_msw', label: 'MSW' },
    { k: 'offers_hha', label: 'HHA' },
  ];
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {items.map((it) => (
        <span key={it.k} className={`text-[9.5px] px-1.5 py-0.5 rounded ${row[it.k] ? 'bg-[#E5F0EA] text-[#2D5F4F]' : 'bg-[#F5EFDD] text-[#A8B0AA]'}`}>{it.label}</span>
      ))}
    </div>
  );
}

export default function PostAcuteTab({ kind, ccn }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const fn = kind === 'hospital' ? api.hospitalPostAcute : api.facilityPostAcute;
    fn(ccn).then((d) => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [kind, ccn]);

  if (loading) return <section className="mt-7"><div className="text-[13px] text-[#7A857F]">Loading post-acute network…</div></section>;

  const hha = data?.home_health || [];
  const hospice = data?.hospice || [];
  const hhaCount = data?.home_health_count ?? hha.length;
  const hospiceCount = data?.hospice_count ?? hospice.length;
  const total = hhaCount + hospiceCount;

  return (
    <section className="mt-7" data-testid="post-acute-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <div className="p-4 rounded-xl bg-white border border-[#E5DCC4]">
          <div className="text-[10.5px] uppercase tracking-wider text-[#4A7C5E] flex items-center gap-1.5"><Home size={12} /> Home Health</div>
          <div className="mt-1 font-display text-2xl text-[#1A2622] tabular-nums" data-testid="post-acute-hha-count">{hhaCount}</div>
          <div className="text-[11px] text-[#7A857F]">agencies within {data?.radius_mi || 25} mi</div>
        </div>
        <div className="p-4 rounded-xl bg-white border border-[#E5DCC4]">
          <div className="text-[10.5px] uppercase tracking-wider text-[#A06628] flex items-center gap-1.5"><HeartPulse size={12} /> Hospice</div>
          <div className="mt-1 font-display text-2xl text-[#1A2622] tabular-nums" data-testid="post-acute-hospice-count">{hospiceCount}</div>
          <div className="text-[11px] text-[#7A857F]">agencies within {data?.radius_mi || 25} mi</div>
        </div>
        <div className="p-4 rounded-xl bg-[#1A2622] text-[#FBF6EC]">
          <div className="text-[10.5px] uppercase tracking-wider text-[#D4A85C]">Continuum partners</div>
          <div className="mt-1 font-display text-2xl tabular-nums">{total}</div>
          <div className="text-[11px] opacity-80">Post-acute providers cohort</div>
        </div>
      </div>

      {total === 0 && (
        <div className="rounded-xl bg-white border border-[#E5DCC4] p-6 text-center text-[13px] text-[#7A857F]">
          No nearby home-health or hospice agencies indexed yet. Once ingest completes, this view will show the nearest 15 of each.
        </div>
      )}

      {hha.length > 0 && (
        <div className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
          <div className="px-5 py-3 border-b border-[#F0E7D2] flex items-center gap-2 flex-wrap">
            <Home size={14} className="text-[#4A7C5E]" />
            <h3 className="font-display text-base font-semibold text-[#1A2622]">Home Health Agencies in network</h3>
            <Link to="/home-health" className="ml-auto text-[11.5px] text-[#34538F] inline-flex items-center gap-1 hover:underline">All home health <ArrowUpRight size={11} /></Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
                <tr>
                  <th className="text-left px-4 py-2.5">Agency</th>
                  <th className="text-left px-4 py-2.5">City</th>
                  <th className="text-left px-4 py-2.5">Distance</th>
                  <th className="text-left px-4 py-2.5">Ownership</th>
                  <th className="text-left px-4 py-2.5">Services</th>
                  <th className="text-right px-4 py-2.5">Quality</th>
                </tr>
              </thead>
              <tbody>
                {hha.map((a) => (
                  <tr key={a.ccn} className="border-t border-[#F0E7D2] hover:bg-[#FBF6EC]" data-testid={`pa-hha-${a.ccn}`}>
                    <td className="px-4 py-2.5">
                      <Link to={`/home-health/${a.ccn}`} className="text-[#1A2622] font-medium hover:text-[#34538F]">{a.name}</Link>
                      <div className="text-[10.5px] text-[#7A857F]">CCN {a.ccn}</div>
                    </td>
                    <td className="px-4 py-2.5 text-[#3A4A44]">{a.city || '—'}, {a.state || ''}</td>
                    <td className="px-4 py-2.5 text-[#3A4A44]"><span className="inline-flex items-center gap-1"><MapPin size={11} className="text-[#4A7C5E]" />{a.distance_mi} mi</span></td>
                    <td className="px-4 py-2.5"><Pill>{a.ownership || '—'}</Pill></td>
                    <td className="px-4 py-2.5"><ServiceDots row={a} /></td>
                    <td className="px-4 py-2.5 text-right"><Star r={a.quality_rating} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {hospice.length > 0 && (
        <div className="mt-5 rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
          <div className="px-5 py-3 border-b border-[#F0E7D2] flex items-center gap-2 flex-wrap">
            <HeartPulse size={14} className="text-[#A06628]" />
            <h3 className="font-display text-base font-semibold text-[#1A2622]">Hospice Agencies in network</h3>
            <Link to="/hospice" className="ml-auto text-[11.5px] text-[#34538F] inline-flex items-center gap-1 hover:underline">All hospices <ArrowUpRight size={11} /></Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
                <tr>
                  <th className="text-left px-4 py-2.5">Agency</th>
                  <th className="text-left px-4 py-2.5">City</th>
                  <th className="text-left px-4 py-2.5">Distance</th>
                  <th className="text-left px-4 py-2.5">Ownership</th>
                  <th className="text-left px-4 py-2.5">Profit status</th>
                  <th className="text-left px-4 py-2.5">Phone</th>
                </tr>
              </thead>
              <tbody>
                {hospice.map((a) => (
                  <tr key={a.ccn} className="border-t border-[#F0E7D2] hover:bg-[#FBF6EC]" data-testid={`pa-hospice-${a.ccn}`}>
                    <td className="px-4 py-2.5">
                      <Link to={`/hospice/${a.ccn}`} className="text-[#1A2622] font-medium hover:text-[#34538F]">{a.name}</Link>
                      <div className="text-[10.5px] text-[#7A857F]">CCN {a.ccn}</div>
                    </td>
                    <td className="px-4 py-2.5 text-[#3A4A44]">{a.city || '—'}, {a.state || ''}</td>
                    <td className="px-4 py-2.5 text-[#3A4A44]"><span className="inline-flex items-center gap-1"><MapPin size={11} className="text-[#A06628]" />{a.distance_mi} mi</span></td>
                    <td className="px-4 py-2.5"><Pill>{a.ownership || '—'}</Pill></td>
                    <td className="px-4 py-2.5"><Pill tone={a.profit_status === 'For Profit' ? 'amber' : 'green'}>{a.profit_status || '—'}</Pill></td>
                    <td className="px-4 py-2.5 text-[11px] text-[#7A857F]">{a.phone || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-5 flex items-start gap-2 text-[11.5px] text-[#A8B0AA] leading-relaxed">
        <Info size={12} className="mt-0.5 shrink-0" />
        <span>
          Nearby = haversine distance ≤ {data?.radius_mi || 25} mi between this {kind === 'hospital' ? 'hospital' : 'facility'} and each agency's ZIP centroid.
          Top 15 nearest of each shown. <strong>Source:</strong> CMS Home Health Care Compare (6jpm-sxkc) + CMS Hospice General Information (yc9t-dgbk).
        </span>
      </div>
    </section>
  );
}
